import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Hero from '../../sections/Hero';
import { blogPosts } from './blogData';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const BlogListPage: React.FC = () => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC', // dates are stored as YYYY-MM-DD; without this they show a day early in US time zones
    });
  };

  return (
    <>
      <Helmet>
        <title>Project Blog</title>
        <meta
          name="description"
          content="Read about JBR Delivery's latest projects, moving tips, and delivery insights. See how we help customers with furniture installation, junk removal, and moving services."
        />
      </Helmet>

      <Hero
        title="Our Blog"
        subtitle="Project highlights, moving tips, and insights from our delivery and installation experts."
        ctaText="Contact Us"
        showQuickForm={false}
      />

      <Box sx={{ py: 8 }}>
        <Container>
          <Grid container spacing={4}>
            {blogPosts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <StyledCard>
                  <CardActionArea
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={post.featuredImage}
                      alt={post.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          color="primary"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      </Box>
                      <Typography
                        gutterBottom
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                        }}
                      >
                        {post.excerpt}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(post.date)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          {blogPosts.length === 0 && (
            <Box textAlign="center" py={8}>
              <Typography variant="h5" color="text.secondary">
                No blog posts yet. Check back soon!
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default BlogListPage;
